import { Result, Spin } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  useApproveNewRequestFromEmailMutation,
  useApproveContractFromEmailMutation,
  useApproveSalesOrderFromEmailMutation,
} from '../store';
import { useNotification } from '../contexts';
import { extractErrorMessageFromAPI } from '../utils';
import CryptoJS from 'crypto-js';
import { SystemApprovalAction, SystemModule } from '../enums';
import { useApproveActivityFromEmailMutation } from '../store/rtkQuery/activityTracking';
import { useApproveAdvancedPaymentFromEmailMutation } from '../store/rtkQuery/advancedPayment';

interface ApprovalLinkParams extends Record<string, unknown> {
  module?: SystemModule;
  action?: SystemApprovalAction;
  token?: string;
}

export default function RequestApprovalTemplate() {
  const [searchParams] = useSearchParams();
  const [approveAdvancedPayment, approveAdvancedPaymentResponse] =
    useApproveAdvancedPaymentFromEmailMutation();
  const [approveActivity, approveActivityResponse] =
    useApproveActivityFromEmailMutation();
  const [approveTravelRequest, approveTravelRequestResponse] =
    useApproveNewRequestFromEmailMutation();
  const [approveContractRequest, approveContractRequestResponse] =
    useApproveContractFromEmailMutation();
  const [approveSalesOrderFromEmail, approveSalesOrderFromEmailResponse] =
    useApproveSalesOrderFromEmailMutation();
  const token = searchParams.get('token');
  const [queryParams, setQueryParams] = useState<ApprovalLinkParams>({});
  const ref = useRef(true);
  const [isActionTaken, setIsActionTaken] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [decryptionError, setDecryptionError] = useState<string | null>(null);
  const notification = useNotification();
  const [canClose, setCanClose] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [signedTokenConfirmed, setSignedTokenConfirmed] = useState(false);

  useEffect(() => {
    const approveRequest = async (params: ApprovalLinkParams) => {
      try {
        // Check module to determine which API to call
        const module = params?.module;
        const normalizedModule =
          typeof module === 'string' ? module.toLowerCase() : module;
        const isSalesOrderModule =
          normalizedModule === String(SystemModule.SALES_ORDER);

        if (module === SystemModule.CONTRACT_MANAGEMENT) {
          await approveContractRequest({
            ...params,
          }).unwrap();
        } else if (module === SystemModule.CLAIM_MANAGEMENT) {
          await approveTravelRequest({
            ...params,
          }).unwrap();
        } else if (module === SystemModule.ACTIVITY_TRACKING) {
          const activityPayload = params.token
            ? { token: params.token }
            : { ...params };
          const response = await approveActivity(activityPayload).unwrap();
          if (params.token) {
            setQueryParams({
              module,
              action: response.data?.action,
            });
          }
        } else if (isSalesOrderModule) {
          await approveSalesOrderFromEmail({
            ...params,
          }).unwrap();
        } else if (module === SystemModule.ADVANCED_PAYMENT) {
          await approveAdvancedPayment({
            ...params,
          }).unwrap();
        } else {
          throw new Error('Invalid module specified');
        }

        setIsActionTaken(true);
        let remaining = 10;
        const interval = setInterval(() => {
          remaining--;
          setCountdown(remaining);
          if (remaining === 0) {
            setCanClose(true);
            clearInterval(interval);
          }
        }, 1000);
      } catch (err) {
        console.error(err);
        setIsActionTaken(false);
        const errorMsg =
          extractErrorMessageFromAPI(err) ||
          'Error in approving request. Please try again later';
        setErrorMessage(errorMsg);
        notification.showNotification('error', 'Error', errorMsg);
      }
    };

    if (token) {
      try {
        if (token.startsWith('v1.')) {
          const tokenParts = token.split('.');
          if (tokenParts.length !== 6 || !tokenParts[1] || !tokenParts[2]) {
            throw new Error('Invalid signed approval token format');
          }
          const normalizedModule = tokenParts[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/');
          const paddedModule = normalizedModule.padEnd(
            Math.ceil(normalizedModule.length / 4) * 4,
            '=',
          );
          const module = atob(paddedModule) as SystemModule;
          if (module !== SystemModule.ACTIVITY_TRACKING) {
            throw new Error('Unsupported signed approval module');
          }
          const normalizedAction = tokenParts[2]
            .replace(/-/g, '+')
            .replace(/_/g, '/');
          const paddedAction = normalizedAction.padEnd(
            Math.ceil(normalizedAction.length / 4) * 4,
            '=',
          );
          const action = atob(paddedAction) as SystemApprovalAction;
          if (!Object.values(SystemApprovalAction).includes(action)) {
            throw new Error('Unsupported signed approval action');
          }
          setQueryParams({ module, action, token });
          if (signedTokenConfirmed && ref.current) {
            void approveRequest({ module, token });
            ref.current = false;
          }
          return;
        }

        // Decode the token
        const decodedToken = decodeURIComponent(token);

        // Validate token format
        if (!decodedToken?.includes(':')) {
          throw new Error('Invalid token format');
        }

        // split into iv / ciphertext
        const [ivB64, cipherB64] = decodedToken.split(':');

        // Validate both parts exist
        if (!ivB64 || !cipherB64) {
          throw new Error('Invalid token format: missing IV or ciphertext');
        }

        // Parse them into WordArrays
        const iv = CryptoJS.enc.Base64.parse(ivB64);
        const key = CryptoJS.enc.Hex.parse(
          import.meta.env.VITE_APPROVAL_PARAMS_DECRYPTION_KEY,
        );

        const decrypted = CryptoJS.AES.decrypt(cipherB64, key, {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });

        // Convert to UTF8 string
        const txt = decrypted.toString(CryptoJS.enc.Utf8);

        // Validate decrypted text is not empty
        if (!txt || txt.trim().length === 0) {
          throw new Error(
            'Decryption resulted in empty string. Invalid key or corrupted data.',
          );
        }

        // Parse JSON
        let parsedParams: unknown;
        try {
          parsedParams = JSON.parse(txt);
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          console.error('Decrypted text:', txt);
          throw new Error('Failed to parse decrypted data as JSON');
        }

        // Validate parsed params
        if (
          !parsedParams ||
          typeof parsedParams !== 'object' ||
          Array.isArray(parsedParams)
        ) {
          throw new Error('Invalid decrypted parameters format');
        }

        const approvalParams = parsedParams as ApprovalLinkParams;

        setQueryParams(approvalParams);
        if (ref.current) {
          void approveRequest(approvalParams);
          ref.current = false;
          return;
        }
      } catch (e) {
        console.error('Decryption failed', e);
        const errorMessage =
          e instanceof Error
            ? e.message
            : 'Failed to decrypt approval parameters. The link may be invalid or expired.';
        setDecryptionError(errorMessage);
        notification.showNotification('error', 'Error', errorMessage);
      }
    }
  }, [
    token,
    approveAdvancedPayment,
    approveActivity,
    approveTravelRequest,
    approveContractRequest,
    approveSalesOrderFromEmail,
    notification,
    signedTokenConfirmed,
  ]);

  const handleClose = () => {
    window.close();
  };

  let mainContent;

  const isLoading =
    approveTravelRequestResponse.isLoading ||
    approveContractRequestResponse.isLoading ||
    approveActivityResponse.isLoading ||
    approveSalesOrderFromEmailResponse.isLoading ||
    approveAdvancedPaymentResponse.isLoading;

  const isError =
    approveTravelRequestResponse.isError ||
    approveContractRequestResponse.isError ||
    approveActivityResponse.isError ||
    approveSalesOrderFromEmailResponse.isError ||
    approveAdvancedPaymentResponse.isError;
  if (isLoading) {
    mainContent = (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <Spin size="large" />
      </div>
    );
  } else {
    mainContent = (
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        {decryptionError ? (
          <>
            <Result
              status="error"
              title="Invalid Link"
              subTitle={decryptionError}
            />
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded text-white transition w-full bg-blue-600 hover:bg-blue-700"
            >
              Close This Tab
            </button>
          </>
        ) : queryParams.token && queryParams.action && !signedTokenConfirmed ? (
          <>
            <Result
              status="info"
              title={`Confirm ${queryParams.action.toLowerCase()}`}
              subTitle="This secure link can be used only once and will update the request immediately."
            />
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="w-1/2 rounded border border-slate-300 px-4 py-2 text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setSignedTokenConfirmed(true)}
                className="w-1/2 rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </>
        ) : isError || errorMessage ? (
          <>
            <Result
              status="error"
              title="Approval Failed"
              subTitle={
                errorMessage ||
                extractErrorMessageFromAPI(
                  approveTravelRequestResponse.error ||
                    approveContractRequestResponse.error ||
                    approveActivityResponse.error ||
                    approveSalesOrderFromEmailResponse.error ||
                    approveAdvancedPaymentResponse.error,
                ) ||
                'An error occurred while processing your approval request.'
              }
            />
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded text-white transition w-full bg-blue-600 hover:bg-blue-700"
            >
              Close This Tab
            </button>
          </>
        ) : !isError && isActionTaken ? (
          <>
            <Result
              icon={
                queryParams.action === SystemApprovalAction.APPROVE ? (
                  <CheckCircleTwoTone twoToneColor="#52c41a" />
                ) : queryParams.action === SystemApprovalAction.HOLD ? (
                  <CheckCircleTwoTone twoToneColor="#eab308" />
                ) : (
                  <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                )
              }
              status={
                queryParams.action === SystemApprovalAction.APPROVE
                  ? 'success'
                  : queryParams.action === SystemApprovalAction.HOLD
                    ? 'info'
                    : 'error'
              }
              title={
                queryParams.action === SystemApprovalAction.APPROVE
                  ? 'Request Approved'
                  : queryParams.action === SystemApprovalAction.HOLD
                    ? 'Request On Hold'
                    : 'Request Rejected'
              }
            />

            <button
              onClick={handleClose}
              disabled={!canClose}
              className={`px-4 py-2 rounded text-white transition w-full ${
                canClose
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {canClose ? 'Close This Tab' : `Please wait ${countdown}s`}
            </button>
          </>
        ) : (
          <>
            <Result status={'info'} title={'No Action Taken'} />
          </>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      {mainContent}
    </div>
  );
}
